import { useState, useEffect, useRef } from "react";
import { styled, applyClassName } from "@glitz/react";
import { GrLinkPrevious } from "react-icons/gr";
import {
  spacing,
  black,
  green,
  white,
  grey,
  darkGrey,
  lightGrey,
  fontSize,
  fontWeight,
} from "../theme";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import CheckoutPaymentStep from "../components/CheckoutPaymentStep";
import SwishOption from "../components/SwishOption";
import { trackPurchaseFromOrder } from "../lib/tracking";

const API_BASE = process.env.REACT_APP_API_URL || "";

interface OrderLineItem {
  id: number;
  name: string;
  productId: number;
  variationId: number;
  quantity: number;
  price: number;
  subtotal: string;
  subtotalTax: string;
  total: string;
  totalTax: string;
  sku: string;
  meta: { key: string; value: string }[];
}

interface TaxLine {
  label: string;
  ratePercent: string;
  taxTotal: string;
  shippingTaxTotal: string;
}

interface OrderDetails {
  id: number | string;
  orderNumber?: string;
  status: string;
  payment_method?: string;
  payment_method_title?: string;
  currency: string;
  total: string;
  subtotal: string;
  discountTotal: string;
  shippingTotal: string;
  totalTax: string;
  pricesIncludeTax: boolean;
  billing: {
    first_name: string;
    last_name: string;
    email: string;
  };
  lineItems: OrderLineItem[];
  shippingLines: { methodTitle: string; total: string }[];
  couponLines: { code: string; discount: string }[];
  taxLines: TaxLine[];
}

const updateOrderStatus = async (
  orderId: number | string,
  status: string,
  paymentMeta?: { payment_method?: string; payment_method_title?: string; transaction_id?: string }
) => {
  try {
    await fetch(`${API_BASE}/api/orders/${orderId}/status`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status, ...paymentMeta }),
    });
  } catch (e) {
    console.error("Failed to update order status:", e);
  }
};

const formatPrice = (value: string | number) => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  return `${Math.round(num)} kr`;
};

const OrderPayPage = () => {
  const navigate = useNavigate();
  const { orderRef } = useParams<{ orderRef: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const [order, setOrder] = useState<OrderDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<
    "swish" | "stripe" | null
  >(null);
  const handledSuccessRef = useRef(false);
  const stripeReturnSyncRef = useRef<string | null>(null);

  const publicBase = process.env.PUBLIC_URL || "";

  useEffect(() => {
    if (!orderRef) return;
    const fetchOrder = async () => {
      try {
        const res = await fetch(
          `${API_BASE}/api/orders/${orderRef}/details`
        );
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          setError(data.error || "Kunde inte hämta ordern");
          return;
        }
        const data: OrderDetails = await res.json();
        setOrder(data);
      } catch {
        setError("Kunde inte hämta ordern. Försök igen senare.");
      } finally {
        setLoading(false);
      }
    };
    fetchOrder();
  }, [orderRef]);

  useEffect(() => {
    if (!order) return;
    const paymentIntentId = searchParams.get("payment_intent");
    const redirectStatus = searchParams.get("redirect_status");

    if (paymentIntentId && !handledSuccessRef.current) {
      if (stripeReturnSyncRef.current === paymentIntentId) {
        return () => {};
      }
      stripeReturnSyncRef.current = paymentIntentId;
      let cancelled = false;
      (async () => {
        let attempts = 0;
        while (!cancelled && attempts < 24) {
          const res = await fetch(`${API_BASE}/api/payments/stripe/sync-order`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ paymentIntentId }),
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.ok) {
            if (!cancelled) {
              handledSuccessRef.current = true;
              setPaymentMethod("stripe");
              setPaymentComplete(true);
              await trackPurchaseFromOrder(order.id);
              setSearchParams({}, { replace: true });
            }
            return;
          }
          if (res.status === 202) {
            attempts += 1;
            await new Promise((r) => setTimeout(r, 1250));
            continue;
          }
          break;
        }
        if (!cancelled && redirectStatus === "succeeded" && !handledSuccessRef.current) {
          handledSuccessRef.current = true;
          await updateOrderStatus(order.id, "processing", {
            payment_method: "stripe",
            payment_method_title: "Stripe",
            transaction_id: paymentIntentId,
          });
          setPaymentMethod("stripe");
          setPaymentComplete(true);
          await trackPurchaseFromOrder(order.id);
          setSearchParams({}, { replace: true });
        }
      })();
      return () => {
        cancelled = true;
        stripeReturnSyncRef.current = null;
      };
    }

    if (redirectStatus === "succeeded" && !handledSuccessRef.current) {
      handledSuccessRef.current = true;
      setPaymentMethod("stripe");
      setPaymentComplete(true);
      void trackPurchaseFromOrder(order.id);
      updateOrderStatus(order.id, "processing", {
        payment_method: "stripe",
        payment_method_title: "Stripe",
        transaction_id: searchParams.get("payment_intent") || undefined,
      });
      setSearchParams({}, { replace: true });
    }
  }, [order, searchParams, setSearchParams]);

  const displayOrderNumber =
    (order && (order.orderNumber || String(order.id))) || "";
  const orderIsSwish =
    paymentMethod === "swish" ||
    String(order?.payment_method || "").toLowerCase() === "swish";

  /** Obetald order → betalningsval. on-hold utan Swish (Flow-default) räknas som obetald. */
  const showPaymentOptions =
    !!order &&
    !paymentComplete &&
    (order.status === "pending" ||
      (order.status === "on-hold" && !orderIsSwish));

  /** Legacy manuell Swish som fortfarande väntar på registrering. */
  const showSwishOrderConfirmation =
    order !== null && order.status === "on-hold" && orderIsSwish;

  const showPaidConfirmation =
    (paymentComplete && (paymentMethod === "stripe" || paymentMethod === "swish")) ||
    (order !== null &&
      order.status === "processing" &&
      (orderIsSwish || String(order.payment_method || "").toLowerCase() === "stripe"));

  const showStripePaymentConfirmation = showPaidConfirmation;

  const subtotalIncTax = order
    ? order.lineItems.reduce(
        (sum, item) =>
          sum + parseFloat(item.subtotal) + parseFloat(item.subtotalTax),
        0
      )
    : 0;
  const totalItemsIncTax = order
    ? order.lineItems.reduce(
        (sum, item) =>
          sum + parseFloat(item.total) + parseFloat(item.totalTax),
        0
      )
    : 0;
  const discountAmount = subtotalIncTax - totalItemsIncTax;
  const shippingAmount = order ? parseFloat(order.shippingTotal) : 0;

  const stripeReturnUrl = order
    ? `${window.location.origin}${publicBase}/order/${orderRef}`
    : undefined;

  return (
    <PageWrapper>
      <PageHeader>
        <IconWrapper onClick={() => navigate("/")}>
          <Icon />
          Tillbaka till shoppen
        </IconWrapper>
        <ImgWrapper>
          <Img
            src={`${process.env.PUBLIC_URL || ""}/trirebels_logo.png`}
            alt="Logo"
            onClick={() => navigate("/")}
          />
        </ImgWrapper>
        <Right />
      </PageHeader>

      {loading && <StatusMessage>Hämtar order…</StatusMessage>}

      {error && (
        <StatusMessage>
          <ErrorText>{error}</ErrorText>
        </StatusMessage>
      )}

      {order && !loading && (
        <>
          <PageTitle>
            Order #{displayOrderNumber}
          </PageTitle>

          {order &&
            !showPaymentOptions &&
            !showSwishOrderConfirmation &&
            !showStripePaymentConfirmation && (
            <StatusBadge>
              Orderstatus: {statusLabel(order.status)}
            </StatusBadge>
          )}

          <ContentWrapper>
            <OrderSection>
              <SectionTitle>Orderinnehåll</SectionTitle>
              <ItemsTable>
                <thead>
                  <TableHeaderRow>
                    <ThProduct>Produkt</ThProduct>
                    <ThRight>Antal</ThRight>
                    <ThRight>Pris</ThRight>
                    <ThRight>Summa</ThRight>
                  </TableHeaderRow>
                </thead>
                <tbody>
                  {order.lineItems.map((item) => {
                    const lineSubtotal =
                      parseFloat(item.subtotal) + parseFloat(item.subtotalTax);
                    const unitPrice = lineSubtotal / item.quantity;
                    return (
                      <ItemRow key={item.id}>
                        <TdProduct>
                          <ItemName>{item.name}</ItemName>
                          {item.meta.length > 0 && (
                            <ItemMeta>
                              {item.meta
                                .map((m) => `${m.key}: ${m.value}`)
                                .join(", ")}
                            </ItemMeta>
                          )}
                        </TdProduct>
                        <TdRight>{item.quantity} x</TdRight>
                        <TdRight>{formatPrice(unitPrice)}</TdRight>
                        <TdRight>{formatPrice(lineSubtotal)}</TdRight>
                      </ItemRow>
                    );
                  })}
                </tbody>
              </ItemsTable>

              <TotalsSection>
                <TotalsRow>
                  <TotalsLabel>Delsumma</TotalsLabel>
                  <TotalsValue>{formatPrice(subtotalIncTax)}</TotalsValue>
                </TotalsRow>

                {order.taxLines.length > 0 &&
                  order.taxLines.map((tax, i) => {
                    const taxAmount =
                      parseFloat(tax.taxTotal) +
                      parseFloat(tax.shippingTaxTotal);
                    if (taxAmount === 0) return null;
                    const pct = parseFloat(tax.ratePercent);
                    return (
                      <TotalsRow key={i}>
                        <TotalsLabel>
                          varav moms {pct > 0 ? `(${pct}%)` : ""}
                        </TotalsLabel>
                        <TotalsValue>{formatPrice(taxAmount)}</TotalsValue>
                      </TotalsRow>
                    );
                  })}

                {discountAmount > 0 && (
                  <TotalsRow>
                    <TotalsLabel>
                      Rabatt
                      {order.couponLines.length > 0 && (
                        <CouponCode>
                          {" "}
                          ({order.couponLines.map((c) => c.code).join(", ")})
                        </CouponCode>
                      )}
                    </TotalsLabel>
                    <DiscountValue>
                      -{formatPrice(order.discountTotal)}
                    </DiscountValue>
                  </TotalsRow>
                )}

                {shippingAmount > 0 && (
                  <TotalsRow>
                    <TotalsLabel>
                      Frakt
                      {order.shippingLines.length > 0 && (
                        <ShippingMethod>
                          {" "}
                          ({order.shippingLines[0].methodTitle})
                        </ShippingMethod>
                      )}
                    </TotalsLabel>
                    <TotalsValue>
                      {formatPrice(order.shippingTotal)}
                    </TotalsValue>
                  </TotalsRow>
                )}

                {shippingAmount === 0 && order.shippingLines.length > 0 && (
                  <TotalsRow>
                    <TotalsLabel>Frakt</TotalsLabel>
                    <FreeShippingValue>Fri frakt</FreeShippingValue>
                  </TotalsRow>
                )}

              

                <TotalRow>
                  <TotalLabel>Totalt</TotalLabel>
                  <TotalValue>{formatPrice(order.total)}</TotalValue>
                </TotalRow>
              </TotalsSection>
            </OrderSection>

            {showSwishOrderConfirmation && (
              <ConfirmationSection>
                <ConfirmationTitle>Orderbekräftelse</ConfirmationTitle>
                <ConfirmationText>
                  Tack för din order! Ordernummer: {displayOrderNumber}
                </ConfirmationText>
                <SwishReminderSection>
                  <SwishReminderTitle>
                    Swish-betalningsuppgifter
                  </SwishReminderTitle>
                  <SwishReminderHint>
                    Använd uppgifterna nedan i Swish-appen.
                  </SwishReminderHint>
                  <SwishOption
                    amount={String(Math.round(parseFloat(order.total)))}
                    message={`Order ${displayOrderNumber}`}
                  />
                </SwishReminderSection>
              </ConfirmationSection>
            )}

            {showStripePaymentConfirmation && (
              <ConfirmationSection>
                <ConfirmationTitle>Tack för din betalning!</ConfirmationTitle>
                <ConfirmationText>
                  Order #{displayOrderNumber} är nu betald
                  {orderIsSwish ? " med Swish" : ""}.
                </ConfirmationText>
              </ConfirmationSection>
            )}

            {showPaymentOptions && (
              <PaymentSection>
                <CheckoutPaymentStep
                  orderId={order.id}
                  orderNumber={displayOrderNumber}
                  returnUrl={stripeReturnUrl}
                  onCompleted={async (method) => {
                    await trackPurchaseFromOrder(order.id);
                    setPaymentMethod(method ?? null);
                    setPaymentComplete(true);
                  }}
                />
              </PaymentSection>
            )}

            {order &&
              !showPaymentOptions &&
              !showSwishOrderConfirmation &&
              !showStripePaymentConfirmation && (
              <AlreadyPaidSection>
                <AlreadyPaidText>
                  Denna order är redan hanterad ({statusLabel(order.status)}).
                </AlreadyPaidText>
              </AlreadyPaidSection>
            )}
          </ContentWrapper>
        </>
      )}
    </PageWrapper>
  );
};

function statusLabel(status: string): string {
  const map: Record<string, string> = {
    pending: "Inväntar betalning",
    "on-hold": "Pausad",
    processing: "Behandlas",
    completed: "Slutförd",
    cancelled: "Avbruten",
    refunded: "Återbetald",
    failed: "Misslyckad",
  };
  return map[status] || status;
}

const PageWrapper = styled.div({
  minHeight: "100vh",
  marginBottom: spacing.xl,
});

const PageHeader = styled.div({
  display: "flex",
  padding: { x: spacing.xl, y: spacing.m },
  backgroundColor: white,
});

const IconWrapper = styled.div({
  display: "flex",
  alignItems: "center",
  gap: spacing.xs,
  cursor: "pointer",
  width: "25%",
  color: black,
  ":hover": { color: green },
});

const Icon = styled(applyClassName(GrLinkPrevious), {
  fontSize: "30px",
});

const ImgWrapper = styled.div({
  width: "50%",
  display: "flex",
  justifyContent: "center",
});

const Img = styled.img({
  width: "75px",
  height: "75px",
  objectFit: "cover",
  padding: { xy: spacing.xs },
  cursor: "pointer",
});

const Right = styled.div({ width: "25%" });

const PageTitle = styled.h1({
  textAlign: "center",
  marginBottom: spacing.xs,
});

const StatusBadge = styled.div({
  textAlign: "center",
  marginBottom: spacing.l,
  fontSize: fontSize.m,
  color: darkGrey,
});

const StatusMessage = styled.div({
  textAlign: "center",
  marginTop: spacing.xxl,
  fontSize: fontSize.l,
});

const ErrorText = styled.span({ color: "#c00" });

const ContentWrapper = styled.div({
  maxWidth: "600px",
  margin: { x: "auto" },
  padding: { x: spacing.m },
});

const OrderSection = styled.div({
  marginBottom: spacing.l,
});

const SectionTitle = styled.h2({
  fontSize: fontSize.l,
  marginBottom: spacing.m,
});

const ItemsTable = styled.table({
  width: "100%",
  borderCollapse: "collapse",
});

const TableHeaderRow = styled.tr({
  borderBottom: { style: "solid", color: grey, width: "2px" },
});

const ThProduct = styled.th({
  textAlign: "left",
  padding: { y: spacing.xs },
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
});

const ThRight = styled.th({
  textAlign: "right",
  padding: { y: spacing.xs },
  fontSize: fontSize.s,
  fontWeight: fontWeight.bold,
});

const ItemRow = styled.tr({
  borderBottom: { style: "solid", color: lightGrey, width: "1px" },
});

const TdProduct = styled.td({
  padding: { y: spacing.s },
});

const TdRight = styled.td({
  textAlign: "right",
  padding: { y: spacing.s },
  fontSize: fontSize.m,
  whiteSpace: "nowrap",
});

const ItemName = styled.div({
  fontSize: fontSize.m,
  fontWeight: fontWeight.regular,
});

const ItemMeta = styled.div({
  fontSize: fontSize.xs,
  color: darkGrey,
  marginTop: spacing.xxs,
});

const TotalsSection = styled.div({
  marginTop: spacing.m,
  paddingTop: spacing.m,
  display: "flex",
  flexDirection: "column",
  gap: spacing.xs,
});

const TotalsRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  fontSize: fontSize.m,
});

const TotalsLabel = styled.span({ color: darkGrey });
const TotalsValue = styled.span({});
const DiscountValue = styled.span({ color: green });
const FreeShippingValue = styled.span({ color: green });
const CouponCode = styled.span({ fontSize: fontSize.xs, color: darkGrey });
const ShippingMethod = styled.span({ fontSize: fontSize.xs, color: darkGrey });


const TotalRow = styled.div({
  display: "flex",
  justifyContent: "space-between",
  fontSize: fontSize.l,
  fontWeight: fontWeight.bold,
  borderTop: { style: "solid", color: grey, width: "2px" },
  paddingTop: spacing.s,
  marginTop: spacing.xs,
});

const TotalLabel = styled.span({});
const TotalValue = styled.span({});

const PaymentSection = styled.div({
  marginTop: spacing.l,
});

const ConfirmationSection = styled.div({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: spacing.l,
  padding: { y: spacing.l },
});

const ConfirmationTitle = styled.h2({
  margin: { top: 0, bottom: 0 },
  textAlign: "center",
  color: green,
});

const ConfirmationText = styled.p({
  margin: { top: 0, bottom: 0 },
  textAlign: "center",
  fontSize: fontSize.m,
});

const SwishReminderSection = styled.div({
  width: "100%",
  maxWidth: "400px",
  marginTop: spacing.m,
  padding: { x: spacing.l, y: spacing.l },
  backgroundColor: "#f5f5f5",
  borderRadius: "8px",
});

const SwishReminderTitle = styled.h3({
  margin: { top: 0, bottom: spacing.xs },
  fontSize: fontSize.m,
});

const SwishReminderHint = styled.p({
  margin: { top: 0, bottom: spacing.m },
  fontSize: fontSize.s,
  color: "#666",
});

const AlreadyPaidSection = styled.div({
  textAlign: "center",
  padding: { y: spacing.xl },
});

const AlreadyPaidText = styled.p({
  fontSize: fontSize.m,
  color: darkGrey,
});

export default OrderPayPage;
